import React from 'react';
import PropTypes from 'prop-types';

// Utils
import { omit, sortBy } from 'lodash';
import { useTranslation } from 'next-i18next';
import MicroModal from 'micromodal';
import { useAppContext } from '../../../src/store/AppContext';
import cn from '../../../src/utils/cn';
import modalConfig from '../../../src/utils/modalConfig';

// Components
import Flag from '../../atoms/Flag/Flag';

import styles from './OffersSlider.module.scss';

function OffersSlider({
  checkboxes, selection, setSelection, emptyOther, isMobile,
}) {
  const { t } = useTranslation();
  const [{ currentPlayer, players, hosting }, stateDispatch] = useAppContext();

  const itemCanBeUnchecked = (checkboxWeekId) => {
    let weekIds = [];
    selection.forEach((s) => {
      const box = checkboxes.find((ch) => ch.id === s);
      if (box) weekIds.push(box.weekId);
    });
    weekIds = sortBy(weekIds);
    const indexPosition = weekIds.findIndex((w) => w === checkboxWeekId);
    return indexPosition === 0 || indexPosition === weekIds.length - 1;
  };

  const handleChange = (checkboxId, checkboxWeekId) => {
    const updated = [...selection];

    if (updated.includes(checkboxId)) {
      // remove item only if it's the first or the last (can't remove a date in the middle)
      if (itemCanBeUnchecked(checkboxWeekId)) {
        const index = updated.findIndex((id) => id === checkboxId);
        if (index > -1) updated.splice(index, 1);
        setSelection(updated);
      }
    } else {
      // add item
      setSelection([...selection, checkboxId]);
    }

    emptyOther([]);
  };

  const getDeletionRisk = () => {
    let ongoingModifications = false;
    const currentHasOptionsOrHosting = currentPlayer?.options?.length
      || currentPlayer?.otherOptions?.length
      || hosting?.length;

    if (players) {
      if (players.length > 1) {
        // multi players
        const otherPlayerHasCamp = players.filter((p) => p.id !== 1).some((p) => p.stage);
        ongoingModifications = otherPlayerHasCamp || currentHasOptionsOrHosting;
      } else {
        // single player
        ongoingModifications = currentHasOptionsOrHosting;
      }
    }

    return ongoingModifications;
  };

  const blankPlayer = {
    stage: null,
    options: null,
    otherOptions: null,
    completed: false,
    sexId: null,
    tennisData: {
      startYear: '',
      weeklyHours: '',
      yearlyMatches: '',
    },
  };

  const removeAllPlayersChoices = () => {
    const updatedPlayers = [...players].map((player) => ({
      ...omit(player, ['assignedTo']),
      ...blankPlayer,
    }));
    stateDispatch({ type: 'SET_PLAYERS', payload: updatedPlayers });
    stateDispatch({ type: 'SET_HOSTING', payload: null });
    stateDispatch({ type: 'SET_EXTRAS_HOSTING', payload: null });
  };

  const checkBeforeChange = (checkboxId, checkboxWeekId) => {
    // before change, check if current player is first player and other players have selection
    if (getDeletionRisk()) {
      const modal = document.querySelector('#modal-warning-first-player');
      if (modal) {
        MicroModal.show('modal-warning-first-player', {
          ...modalConfig,
          onClose: () => {
            if (modal.dataset.reset) {
              // if confirm, remove all players choices and apply changes for the first player
              removeAllPlayersChoices();
              handleChange(checkboxId, checkboxWeekId);
            }
          },
        });
      }
    } else {
      handleChange(checkboxId, checkboxWeekId);
    }
  };

  const isDisabled = (checkbox) => checkbox.disabled && !selection.includes(checkbox.id);

  return (
    <div className={styles.slider_wrapper}>
      {checkboxes?.length ? (
        <ul
          className={cn([
            'flex a-end p-b-15',
            isMobile ? 'd-col a-start' : '',
          ])}
        >
          {checkboxes
            .sort((a, b) => a.weekId - b.weekId)
            .map((checkbox) => (
              <li
                key={checkbox.id}
                className={cn([
                  styles.checkbox,
                  isMobile ? styles.is_mobile : '',
                  isDisabled(checkbox) || checkbox.flag === 'SOLD_OUT' ? styles.disabled : '',
                  checkbox.flag === 'SOLD_OUT' ? styles.sold_out : '',
                  'flex d-col',
                ])}
              >
                {checkbox.flag && (checkbox.flag === 'LAST_MINUTE' || checkbox.flag === 'LAST_PLACES') ? (
                  <div className="m-b-5">
                    <Flag color="orange">{t(`offer.flag.${checkbox.flag.toLowerCase()}`)}</Flag>
                  </div>
                ) : null}
                <input
                  type="checkbox"
                  name={checkbox.id}
                  id={checkbox.id}
                  checked={selection.includes(checkbox.id)}
                  onChange={() => checkBeforeChange(checkbox.id, checkbox.weekId)}
                  disabled={
                      isDisabled(checkbox)
                      || checkbox.flag === 'SOLD_OUT'
                      || (currentPlayer?.id !== 1)
                    }
                />
                <label
                  htmlFor={checkbox.id}
                  className={cn([
                    checkbox.sublabel ? styles.with_sublabel : '',
                    currentPlayer?.id !== 1 ? styles.disabled_player : '',
                  ])}
                  data-tip={t('package.offers.selection.needUncheck')}
                  data-for="needUncheck"
                  data-tip-disable={!isDisabled(checkbox)}
                >
                  <div
                    className={cn([
                      styles.box,
                      isMobile ? styles.mobile_check : '',
                      currentPlayer?.id !== 1 ? styles.disabled_box : '',
                    ])}
                  />
                  <div className={checkbox.flag === 'SOLD_OUT' ? styles.sold_out : ''}>
                    {checkbox.sublabel ? <span className="fw-400 p-b-5">{checkbox.sublabel}</span> : null}
                    <span className={styles.sold_out_label}>
                      {checkbox.flag === 'SOLD_OUT' ? t('package.outOfStock') : checkbox.label}
                    </span>
                  </div>
                </label>
              </li>
            ))}
        </ul>
      ) : null}
    </div>
  );
}

OffersSlider.propTypes = {
  checkboxes: PropTypes.arrayOf(PropTypes.shape({})),
  selection: PropTypes.arrayOf(PropTypes.string),
  setSelection: PropTypes.func,
  emptyOther: PropTypes.func,
  isMobile: PropTypes.bool,
};

OffersSlider.defaultProps = {
  checkboxes: [],
  selection: [],
  setSelection: () => {},
  emptyOther: () => {},
  isMobile: false,
};

export default OffersSlider;
